/**
 * OneSignal service module.
 * Handles init, login/logout, tags. Called from AuthContext + App.
 *
 * Subscription model: one OneSignal user can have many subscriptions (devices/browsers).
 * We link them via external_id = our user.id.
 */
import axios from 'axios';
import OneSignal from 'react-onesignal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _initPromise = null;
let _initialized = false;
let _appId = null;

/**
 * Initialize OneSignal SDK once per page load.
 * Idempotent: safe to call multiple times (React StrictMode double-mount safe).
 */
export async function initOneSignal() {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    try {
      const { data } = await axios.get(`${API}/onesignal/config`);
      if (!data?.enabled || !data?.app_id) {
        console.info('[OneSignal] Not configured — skipping init');
        return false;
      }
      _appId = data.app_id;

      await OneSignal.init({
        appId: data.app_id,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: 'push',
                autoPrompt: false, // we trigger manually after login
                text: {
                  actionMessage:
                    'Attiva le notifiche per ricevere aggiornamenti sui tuoi contratti, schedine scaricate e eventi nella tua città.',
                  acceptButton: 'Sì, attiva',
                  cancelButton: 'Più tardi',
                },
                delay: { pageViews: 1, timeDelay: 0 },
              },
            ],
          },
        },
      });

      _initialized = true;
      console.info('[OneSignal] Initialized');
      return true;
    } catch (e) {
      console.warn('[OneSignal] Init failed:', e?.message || e);
      return false;
    }
  })();

  return _initPromise;
}

/**
 * Link current subscription to our app user (called after login).
 * Also sets standard tags for segmentation from OneSignal dashboard.
 */
export async function identifyOneSignalUser(user) {
  if (!user?.id) return;
  try {
    await initOneSignal();
    if (!_initialized) return;

    await OneSignal.login(String(user.id));

    const tags = {
      plan_type: user?.subscription?.plan_type || 'trial',
      is_active: user?.subscription?.is_active ? 'true' : 'false',
      language: user?.preferred_language || 'ITA',
      role: user?.role || 'user',
    };
    if (user?.email) tags.email = user.email;

    await OneSignal.User.addTags(tags);
    console.info('[OneSignal] Identified user', user.id, tags);
  } catch (e) {
    console.warn('[OneSignal] Identify failed:', e?.message || e);
  }
}

/**
 * Unlink subscription from user (called on logout).
 */
export async function logoutOneSignalUser() {
  try {
    if (!_initialized) return;
    await OneSignal.logout();
    console.info('[OneSignal] User logged out');
  } catch (e) {
    console.warn('[OneSignal] Logout failed:', e?.message || e);
  }
}

/**
 * Trigger the soft slidedown prompt manually (from UI buttons/banners).
 * Shows our custom message first, then the native browser prompt if user accepts.
 */
export async function promptForPushPermission() {
  try {
    await initOneSignal();
    if (!_initialized) return false;
    await OneSignal.Slidedown.promptPush();
    return true;
  } catch (e) {
    console.warn('[OneSignal] Prompt failed:', e?.message || e);
    return false;
  }
}

/**
 * Returns true if the user has already subscribed (opted-in to push).
 * Used to hide the recovery banner after opt-in.
 */
export function isPushSubscribed() {
  try {
    if (!_initialized) return false;
    return Boolean(OneSignal.User?.PushSubscription?.optedIn);
  } catch {
    return false;
  }
}

/**
 * Returns 'granted' | 'denied' | 'default' (native permission state).
 */
export function getPermissionState() {
  try {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  } catch {
    return 'unsupported';
  }
}
